import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Copy, Check, Key, Info, Globe, ShieldAlert } from "lucide-react";
import { VMProvisionForm } from "@/components/vm/VMProvisionForm";
import { useTemplates, useCreateVM } from "@/hooks/useVMs";
import { useCapacity } from "@/hooks/useCapacity";
import { getProvisioningDefaults } from "@/api/endpoints/provisioning";
import type { VM } from "@/types";

export default function Provisioning() {
  const navigate = useNavigate();
  const { data: templates } = useTemplates();
  const { data: capacity } = useCapacity();
  const { data: defaults } = useQuery({
    queryKey: ["provisioning", "defaults"],
    queryFn: getProvisioningDefaults,
    staleTime: 5 * 60_000,
  });
  const createVM = useCreateVM();
  const [createdVM, setCreatedVM] = useState<VM | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  if (createdVM) {
    return (
      <div className="mx-auto max-w-lg space-y-6 pt-12">
        <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-6 text-center">
          <Key className="mx-auto h-10 w-10 text-green-400" />
          <h2 className="mt-4 text-xl font-bold text-gray-100">
            VM Created Successfully
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            <span className="font-medium text-gray-200">{createdVM.name}</span>{" "}
            is now provisioning. Save these credentials — they are the initial
            login for your VM.
          </p>
        </div>

        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6 space-y-4">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
            Initial Credentials
          </h3>

          {/* Username */}
          <div>
            <label className="mb-1 block text-xs text-gray-500">Username</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-gray-800 px-4 py-2.5 font-mono text-sm text-gray-200">
                {createdVM.ci_user ?? "admin"}
              </code>
              <button
                onClick={() => handleCopy(createdVM.ci_user ?? "admin", "user")}
                className="rounded-lg border border-gray-700 p-2.5 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
              >
                {copied === "user" ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="mb-1 block text-xs text-gray-500">Password</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-gray-800 px-4 py-2.5 font-mono text-sm text-gray-200">
                {createdVM.ci_password ?? "—"}
              </code>
              <button
                onClick={() => handleCopy(createdVM.ci_password ?? "", "pass")}
                className="rounded-lg border border-gray-700 p-2.5 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
              >
                {copied === "pass" ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {createdVM.ip_address && (
            <div>
              <label className="mb-1 block text-xs text-gray-500">IP Address</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-gray-800 px-4 py-2.5 font-mono text-sm text-gray-200">
                  {createdVM.ip_address}
                </code>
                <button
                  onClick={() => handleCopy(createdVM.ip_address!, "ip")}
                  className="rounded-lg border border-gray-700 p-2.5 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                >
                  {copied === "ip" ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}
        </div>

        {defaults && (
          <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-5 text-sm">
            <div className="mb-3 flex items-center gap-2 text-blue-300">
              <Info className="h-4 w-4" />
              <span className="font-medium">Cloud-init applique</span>
            </div>
            <dl className="space-y-1.5 text-gray-300">
              <div className="flex gap-2">
                <dt className="min-w-[140px] text-xs text-gray-500">
                  <Globe className="mr-1 inline h-3 w-3" /> DNS
                </dt>
                <dd className="font-mono text-xs">{defaults.nameservers}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="min-w-[140px] text-xs text-gray-500">Search domain</dt>
                <dd className="font-mono text-xs">{defaults.searchdomain}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="min-w-[140px] text-xs text-gray-500">
                  <ShieldAlert className="mr-1 inline h-3 w-3" /> MOTD
                </dt>
                <dd className="text-xs">
                  Disclaimer AlphaProx affiche a la 1ere connexion SSH
                  <span className="mt-1 block font-mono text-[10px] text-gray-500">
                    ({defaults.disclaimer_snippet})
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        )}

        <button
          onClick={() => navigate(`/vms/${createdVM.id}`)}
          className="w-full rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          Go to VM Details
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">
          Provision New VM
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          Configure and deploy a new virtual machine
        </p>
      </div>

      {defaults && (
        <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-3 text-xs text-gray-400">
          <div className="mb-1 flex items-center gap-2 text-gray-300">
            <Info className="h-3.5 w-3.5" />
            <span className="font-medium">Ce qui sera applique automatiquement</span>
          </div>
          <div className="ml-5 space-y-0.5">
            <div>• DNS: <span className="font-mono text-gray-300">{defaults.nameservers}</span></div>
            <div>• Search domain: <span className="font-mono text-gray-300">{defaults.searchdomain}</span></div>
            <div>• Disclaimer MOTD + banner issue.net (cicustom)</div>
          </div>
        </div>
      )}

      <VMProvisionForm
        templates={templates ?? []}
        capacity={capacity ?? []}
        isSubmitting={createVM.isPending}
        onSubmit={(payload) => {
          createVM.mutate(payload, {
            onSuccess: (vm) => {
              setCreatedVM(vm);
            },
          });
        }}
      />

      {createVM.isError && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4">
          <p className="text-sm text-red-400">
            Failed to provision VM: {createVM.error.message}
          </p>
        </div>
      )}
    </div>
  );
}
